import { motion } from 'framer-motion';
import { ShieldCheck, FileText, Compass, ArrowRight, Truck } from 'lucide-react';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import SEO from '../components/ui/SEO';

export default function Jordflytning() {
  const features = [
    {
      title: "Speciale i jordflytning",
      description: "Vi har stor erfaring og speciale inden for flytning af jord for både private og erhvervskunder.",
      icon: Compass
    },
    {
      title: "Håndtering af anmeldelser",
      description: "Vi kan stå for alle de lovpligtige anmeldelser og dokumentation i forbindelse med flytningen af jorden.",
      icon: FileText
    },
    {
      title: "Jordflytning med Grab",
      description: "Med vores moderne grab-biler kan vi nemt og effektivt flytte og læsse jord, selv hvor pladsen er trang.",
      icon: Truck
    }
  ];

  return (
    <>
      <SEO 
        title="Jordflytning" 
        description="NR. GALTEN VOGNMANDSFORRETNING ApS har speciale inden for jordflytning. Vi klarer anmeldelser af jorden og flytter jord med grab i lokalområdet." 
      />

      {/* Hero */}
      <section className="pt-40 pb-20 bg-slate-50 text-slate-900 relative overflow-hidden">
        {/* Skewed Background Element */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[#1d398e] hidden lg:block skew-x-[-12deg] translate-x-20 z-0" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center space-x-2 bg-blue-50 text-[#1d398e] px-4 py-2 rounded-full text-sm font-bold mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1d398e] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1d398e]"></span>
              </span>
              <span className="tracking-wide uppercase">Professionel jordhåndtering</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-[1.1] tracking-wide text-slate-900">
              Effektiv <span className="text-[#1d398e]">jordflytning</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed max-w-2xl">
              Vi har speciale inden for jordflytning og sørger for alt det praktiske – lige fra selve flytningen til de lovpligtige anmeldelser.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content Section */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-xl md:text-3xl font-bold text-blue-900 mb-6">Vi klarer hele processen for dig</h2>
            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p>
                Når der skal flyttes jord i forbindelse med byggeri, anlægsarbejde eller haveprojekter, er der ofte en række regler og krav, der skal overholdes. Hos NR. GALTEN VOGNMANDSFORRETNING ApS har vi speciale inden for jordflytning og kender alle procedurerne.
              </p>
              <p>
                Vi kan lave de nødvendige anmeldelser på flytningen af jorden til kommunen, så du er sikker på, at alt foregår fuldt ud lovligt og efter bogen. Vi sørger for den rette klassificering og dokumentation, så du slipper for bekymringerne.
              </p>
              <p className="font-semibold text-slate-900">
                Har du brug for at få flyttet jord fra svært tilgængelige steder? Vi kan også flytte jord med grab, hvilket gør arbejdet hurtigt og skånsomt for omgivelserne.
              </p>
            </div>
          </div>
          <div className="relative">
            <img 
              src="https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/52ad64ed-5451-45d9-a7a2-f3d8148f1648.jpg" 
              alt="Jordflytning og grus" 
              className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
            />
          </div>
        </div>
      </Section>

      {/* Features Grid */}
      <Section light>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-blue-900 mb-6">
                <feature.icon className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-blue-900 mb-3">{feature.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* Grab Section with another image */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1">
            <img 
              src="https://assets.auxodev.dk/assets/bbd899f1-874b-429b-b5f9-77a2b21d61ba/11d8ac04-b4a2-4792-97df-3ed8383a3251.jpg" 
              alt="Vores lastbil - NR. GALTEN VOGNMANDSFORRETNING ApS" 
              className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
            />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-xl md:text-3xl font-bold text-blue-900 mb-6">Flytning af jord med Grab</h2>
            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p>
                Vores lastbiler er udstyret med kraftige grabber, der gør os i stand til at række over hække, hegn eller andre forhindringer for at hente eller aflevere jord. Det sparer dig for hårdt manuelt arbejde med skovl og trillebør.
              </p>
              <p>
                Uanset om det drejer sig om overskudsjord fra en ny indkørsel, udgravning til en swimmingpool eller havearkitektur, rykker vi hurtigt ud i lokalområdet med det rette materiel.
              </p>
            </div>
            <div className="mt-8">
              <Button to="/kontakt">Få et tilbud på jordflytning</Button>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section dark>
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-xl md:text-3xl font-bold mb-6">Skal vi hjælpe med din jordflytning?</h2>
          <p className="text-blue-100 mb-10">
            Vi rådgiver dig gerne om reglerne for jordflytning og giver et uforpligtende tilbud på opgaven.
          </p>
          <Button to="/kontakt" variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-900">
            Kontakt os i dag <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </Section>
    </>
  );
}
